import React, { useState } from 'react';
import { Box, Typography, TextField, Button, CircularProgress } from '@mui/material';
import { C } from '../theme';

const API = process.env.REACT_APP_API_URL || 'https://api.binko.me';

export default function VenueLogin() {
  const [phone, setPhone]       = useState('');
  const [otp, setOtp]           = useState('');
  const [step, setStep]         = useState('phone'); // 'phone' | 'otp'
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  async function handleSendOtp() {
    setError('');
    if (!phone || phone.length < 10) {
      setError('Enter a valid 10-digit mobile number');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/venue/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to send OTP');
        return;
      }
      setStep('otp');
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyOtp() {
    setError('');
    if (!otp || otp.length !== 6) {
      setError('Enter the 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/venue/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, otp }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Invalid OTP');
        return;
      }
      localStorage.setItem('binko_venue_token', data.token);
      localStorage.setItem('binko_venue_name', data.venueName);
      window.location.href = '/venue/dashboard';
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: C.bgApp,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px 24px',
    }}>
      {/* Logo */}
      <img src="/logo.png" alt="Binko" style={{ height: '40px', width: 'auto', marginBottom: '8px' }} />
      <Typography sx={{ fontSize: '12px', color: C.textMuted, mb: '40px', letterSpacing: '0.05em' }}>
        VENUE PORTAL
      </Typography>

      <Box sx={{
        width: '100%',
        maxWidth: '380px',
        background: C.bgCard,
        borderRadius: '16px',
        border: `0.5px solid ${C.border}`,
        padding: '32px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
      }}>
        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: C.textPrimary }}>
          {step === 'phone' ? 'Sign in to your venue' : 'Enter OTP'}
        </Typography>

        {step === 'phone' ? (
          <>
            <Typography sx={{ fontSize: '13px', color: C.textMuted, mt: '-8px' }}>
              Enter the mobile number registered with Binko for your venue.
            </Typography>
            <TextField
              fullWidth
              placeholder="Mobile number"
              value={phone}
              onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              inputProps={{ inputMode: 'numeric' }}
              sx={inputSx}
            />
            {error && <Typography sx={{ fontSize: '13px', color: C.orange }}>{error}</Typography>}
            <Button
              fullWidth
              onClick={handleSendOtp}
              disabled={loading}
              sx={btnSx}
            >
              {loading ? <CircularProgress size={18} sx={{ color: C.bgApp }} /> : 'Send OTP'}
            </Button>
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: '13px', color: C.textMuted, mt: '-8px' }}>
              OTP sent to +91 {phone}
            </Typography>
            <TextField
              fullWidth
              placeholder="6-digit OTP"
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ inputMode: 'numeric' }}
              sx={inputSx}
            />
            {error && <Typography sx={{ fontSize: '13px', color: C.orange }}>{error}</Typography>}
            <Button
              fullWidth
              onClick={handleVerifyOtp}
              disabled={loading}
              sx={btnSx}
            >
              {loading ? <CircularProgress size={18} sx={{ color: C.bgApp }} /> : 'Verify & Sign in'}
            </Button>
            <Button
              fullWidth
              onClick={() => { setStep('phone'); setOtp(''); setError(''); }}
              sx={{ color: C.textMuted, fontSize: '13px', textTransform: 'none' }}
            >
              Change number
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}

const inputSx = {
  '& .MuiOutlinedInput-root': {
    background: 'rgba(255,255,255,0.04)',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '15px',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
    '&.Mui-focused fieldset': { borderColor: '#0D9DA5' },
  },
  '& input::placeholder': { color: 'rgba(255,255,255,0.3)', opacity: 1 },
};

const btnSx = {
  background: '#FBBC15',
  color: '#000',
  fontWeight: 700,
  fontSize: '15px',
  borderRadius: '10px',
  padding: '12px',
  textTransform: 'none',
  '&:hover': { background: '#e6ad10' },
  '&:disabled': { background: 'rgba(251,188,21,0.4)', color: 'rgba(0,0,0,0.4)' },
};
